import { Injectable, signal } from '@angular/core';
import { ProductType } from '../models/rate-sheet.models';
import {
  buildRatesFilterSnapshot,
  readRatesFilterSnapshot,
  writeRatesFilterSnapshot,
} from './rates-filter-persistence';

/** A case favorites shortlist is a few hand-picked products, not the whole fit
 *  set — the row "Add to case" greys out once this many are on the case. */
export const MAX_CASE_FAVORITES = 10;

/** Carrier/products picker value used by the rates filter chrome.
 *  Per-tab via `_companyFilterByTab` Record; the component's active
 *  `companyFilter` field reads/writes through the tab-switch handler. */
export interface CompanyFilterValue {
  companies: string[];
  products: string[];
}

/** One row of the per-tab profiled-product catalog that feeds the rail's
 *  carrier/product picker.
 *
 *  `productId` is the Benefit API product id for the pair, resolved the same
 *  way the row-enrichment path resolves it (`productid ?? id ?? productId`),
 *  so it shares the ID space of the grid rows' `productId`. It lets the picker
 *  apply id-keyed scopes — e.g. the IA "My Shelf" approved-shelf filter — that
 *  company/product *names* alone can't express. Optional: tabs that don't need
 *  an id-keyed scope may omit it, and consumers must treat a missing id as
 *  "unknown", not "excluded". */
export interface CatalogProductPair {
  company: string;
  product: string;
  productId?: number | null;
  /** Asset class of this specific pair (FA/IA/VA/RILA). Needed because the
   *  Structured ('RILA') tab's catalog merges RILA and VA products, and their
   *  approved-shelf lists are disjoint — the pair's own type, not the tab's,
   *  decides which list applies. Omitted pairs fall back to the tab type. */
  productType?: string;
}

/**
 * Per-tab filter slot — 19 fields, one Record per ProductType.
 *
 * Mirrors the shape produced by `_emptyFilterSlot()` in
 * rates.component.ts today (Phase 3a.2 will migrate the actual storage).
 * Lifted into an explicit interface so consumers (rates.component proxy
 * + future RatesRailComponent) reference a named type instead of the
 * old `ReturnType<RatesComponent['_emptyFilterSlot']>` entanglement.
 */
export interface FilterStateSlot {
  selectedStates: string[];
  selectedIndexes: string[];
  selectedSurrYears: string[];
  selectedCreditingStrategies: string[];
  selectedStrategyNames: string[];
  selectedStrategyCombos: string[];
  contributionAmount: number | null;
  filterRop: boolean;
  filterBailOut: boolean;
  selectedAmBest: string[];
  filterBonus: boolean;
  filterInverse: boolean;
  selectedRiders: string[];
  selectedPremiumTypes: string[];
  filterOwner: boolean;
  filterAnnuitant: boolean;
  // ── RILA/VA filter block (added 2026-06-05) ─────────────────────
  // Six dimensions only applicable on RILA/VA. Hidden on FA/IA via
  // DIM_TAB_APPLICABILITY. The fields below are the per-tab authoritative
  // storage; the legacy single-instance `rilaSelectedBuffers` /
  // `rilaSelectedFloors` / `rilaSelectedDurations` / `rilaSelectedRateTypes` /
  // `rilaSelectedProtection` fields on RatesComponent are now proxies that
  // delegate here (and convert number ↔ "-N%" label for buffer/floor).
  //
  // Labels (selectedBufferLevels / selectedFloorLevels) are formatted as
  // "N%" strings (e.g. "10%") so the rail picker can render them directly.
  // Filter logic converts back to numeric via parseLevelLabel().

  /** Buffer levels selected as "N%" strings, e.g. "10%". */
  selectedBufferLevels: string[];
  /** Floor levels selected as "-N%" strings, e.g. "-5%". */
  selectedFloorLevels: string[];
  /** Segment durations (perChangeTerm values), e.g. "Annual", "2 Year". */
  selectedSegmentDurations: string[];
  /** Rate types: subset of "Cap" | "Par Rate" | "Spread" | "Fixed" | "Perf Trigger". */
  selectedRateTypes: string[];
  /** Single-select protection type: "Buffer Only" | "Floor Only" | "Both" | null. */
  selectedProtectionType: string | null;
  /** Min premium range — inclusive lower bound (null = no lower limit). */
  minPremiumMin: number | null;
  /** Min premium range — inclusive upper bound (null = no upper limit). */
  minPremiumMax: number | null;

  searchText: string;
}

/** Factory for an empty filter slot — same defaults the component used. */
export function emptyFilterStateSlot(): FilterStateSlot {
  return {
    selectedStates: [],
    selectedIndexes: [],
    selectedSurrYears: [],
    selectedCreditingStrategies: [],
    selectedStrategyNames: [],
    selectedStrategyCombos: [],
    contributionAmount: null,
    filterRop: false,
    filterBailOut: false,
    selectedAmBest: [],
    filterBonus: false,
    filterInverse: false,
    selectedRiders: [],
    selectedPremiumTypes: [],
    filterOwner: false,
    filterAnnuitant: false,
    selectedBufferLevels: [],
    selectedFloorLevels: [],
    selectedSegmentDurations: [],
    selectedRateTypes: [],
    selectedProtectionType: null,
    minPremiumMin: null,
    minPremiumMax: null,
    searchText: '',
  };
}

/**
 * Menu-axis Phase 3a — RatesFilterStateService
 *
 * Owns the per-tab filter state for /rates. Both the legacy
 * `.s2-filter-bar` chrome (via RatesComponent's proxy getter/setters)
 * and the future RatesRailComponent (Phase 3b consumer) bind to this
 * service. T-214 invariant preserved: per-ProductType filter Records
 * keyed by `_activeTab`. Tab-switch row carry-over and filter-state
 * leak remain impossible by construction.
 *
 * 3a.1 ships only the scaffolding: active-tab + version signals plus
 * the FilterStateSlot interface and empty-slot factory. Sub-tasks
 * 3a.2 → 3a.6 migrate state and behavior from rates.component.ts
 * one chunk at a time, with smoke green between each.
 */
@Injectable({ providedIn: 'root' })
export class RatesFilterStateService {
  // Field initializers all run before this body (ES class-field semantics), so
  // the Records below exist by the time hydration writes into them.
  constructor() {
    this._hydrateFromStorage();
  }

  // ── Active tab (single source of truth in Phase 3a.6 onward) ────────
  private _activeTab = signal<ProductType>('IA');
  readonly activeTab = this._activeTab.asReadonly();

  setActiveTab(t: ProductType): void {
    this._activeTab.set(t);
    this._bump();
  }

  // ── Change-notification signal — bumps on every state mutation.
  //    Downstream consumers (Phase 3b rail per-tab pill badges, Phase
  //    3d chip stack, etc.) subscribe via Angular signals. RatesComponent
  //    keeps its existing applyFilters() triggers; this signal exists
  //    for net-new consumers, not to re-fire the grid. ──────────────
  private _version = signal(0);
  readonly version = this._version.asReadonly();
  private _bump(): void {
    this._version.update((n) => n + 1);
    this._schedulePersist();
  }

  // ── sessionStorage persistence ──────────────────────────────────────
  //
  //  Survives a browser refresh; see rates-filter-persistence.ts for why
  //  sessionStorage rather than localStorage. Hooked into _bump() so every
  //  slot mutation is covered by construction — the FA signals and the
  //  per-tab carrier Record sit outside the slots and call _schedulePersist()
  //  themselves.
  //
  //  Coalesced onto a microtask: a single rail interaction can bump version
  //  several times (set field → request apply → tab archive), and one
  //  stringify per interaction is enough.
  private _persistScheduled = false;
  /** Suppresses writes while hydrating, so reading storage can't immediately
   *  write a half-built snapshot back over itself. */
  private _hydrating = false;

  private _schedulePersist(): void {
    if (this._persistScheduled || this._hydrating) return;
    this._persistScheduled = true;
    queueMicrotask(() => {
      this._persistScheduled = false;
      this._persistNow();
    });
  }

  private _persistNow(): void {
    writeRatesFilterSnapshot(
      buildRatesFilterSnapshot({
        activeTab: this._activeTab(),
        slots: this._filterStateByTab,
        company: this._companyFilterByTab,
        fa: {
          rateTerms: this._faRateTerms(),
          surrYears: this._faSurrYears(),
          bonusYears: this._faBonusYears(),
          mva: this._faMva(),
          qualified: this._faQualified(),
        },
        floatChanges: this._floatChanges(),
      }),
    );
  }

  private _hydrateFromStorage(): void {
    const snap = readRatesFilterSnapshot();
    if (!snap) return;
    this._hydrating = true;
    try {
      this._filterStateByTab = snap.slots;
      this._companyFilterByTab = snap.company;
      this._faRateTerms.set(snap.fa.rateTerms);
      this._faSurrYears.set(snap.fa.surrYears);
      this._faBonusYears.set(snap.fa.bonusYears);
      this._faMva.set(snap.fa.mva);
      this._faQualified.set(snap.fa.qualified);
      this._floatChanges.set(snap.floatChanges);
      this._activeTab.set(snap.activeTab);
    } finally {
      this._hydrating = false;
    }
    // One bump so anything already subscribed re-reads the restored state.
    this._version.update((n) => n + 1);
  }

  // ── Pending-change count per tab, for the rail pill badges ──────────
  //
  //  Why this needs to exist at all: a scheduled change is rendered inside the
  //  rate cell, and the IA grid holds ~9,400 rows while rendering about 25.
  //  Measured on 2026-08-10, loading each tab unfiltered put ZERO changed rows
  //  in the rendered window on all four — so without a count the feature is
  //  only reachable by someone who already knows the ◆ lens exists.
  //
  //  Set per tab as that tab's feed lands, so a tab that has not loaded shows
  //  no badge rather than a misleading 0.
  //
  //  KNOWN LIMIT: this counts rows in the loaded feed, before the shelf filter.
  //  For a shelf-scoped user it can therefore overstate. Narrowing to the
  //  post-filter count means asking the grid after doesExternalFilterPass has
  //  run, which only answers for the active tab; deliberately not done yet.
  //
  //  NOTE, on the branch this was ported to: the upstream comment here claimed
  //  a blocked product type "cannot reach here, because RatesRailComponent.tabs
  //  already drops those pills entirely via isBlocked()". That is NOT true on
  //  this branch — `tabs` is a static IA/FA/RILA array and the rail does not
  //  reference isBlocked(); the guard belongs to the Standard Views work, which
  //  is not here. It discloses nothing new even so: the pill and its loaded rows
  //  were already visible before the badge existed, so the badge adds a count to
  //  something the user could already see. Revisit if the block guard lands.
  private _changeCounts = signal<Partial<Record<ProductType, number>>>({});
  readonly changeCounts = this._changeCounts.asReadonly();

  setChangeCount(t: ProductType, n: number): void {
    this._changeCounts.update((m) => ({ ...m, [t]: n }));
  }

  // ── Float changed rows to the top ───────────────────────────────────
  //
  //  Deliberately NOT a fifth segment on the Changes switch. That control
  //  (All / ▲ / ▼ / ◆) answers "which rows do I want"; this answers "where do
  //  they go". They are orthogonal and the useful combinations need both —
  //  float only the decreases, or filter to ◆ and keep the natural sort.
  private _floatChanges = signal(false);
  readonly floatChanges = this._floatChanges.asReadonly();

  toggleFloatChanges(): void {
    this._floatChanges.update((v) => !v);
    this._bump();
  }

  // ── Per-tab filter Records (Phase 3a.2 migration target) ────────
  // Each ProductType keeps its own FilterStateSlot. Tab-switch row
  // carry-over and filter-state leak are impossible by construction
  // (T-214 invariant — same Record shape that lived on RatesComponent
  // pre-3a, just relocated). Phase 3a.3 wraps these slots in property
  // getter/setter pairs that consumers bind to.
  private _filterStateByTab: Record<ProductType, FilterStateSlot> = {
    FA: emptyFilterStateSlot(),
    IA: emptyFilterStateSlot(),
    RILA: emptyFilterStateSlot(),
    VA: emptyFilterStateSlot(),
    ALL: emptyFilterStateSlot(),
  };

  /** Returns the FilterStateSlot for a specific tab. Used by the
   *  RatesComponent proxy in Phase 3a.2 (before 3a.6 inverts active-
   *  tab ownership) and by net-new consumers needing explicit-tab
   *  access (e.g. Phase 3a.5 grid-init reads each tab's slot). */
  slotFor(t: ProductType): FilterStateSlot {
    return this._filterStateByTab[t];
  }

  /** Returns the active tab's FilterStateSlot. After Phase 3a.6 this
   *  is the canonical accessor. Until then, callers using slotFor(t)
   *  with explicit ProductType still work. */
  activeSlot(): FilterStateSlot {
    return this._filterStateByTab[this._activeTab()];
  }

  /** Replaces the active tab's slot with a fresh empty one and bumps
   *  version. RatesComponent.resetAllFilters() only clears its own local
   *  mirror fields (used by doesExternalFilterPass); without this, the
   *  rail's toggle pills / chips / accordion summaries — which all read
   *  activeSlot() — kept showing the pre-reset selections after "Clear
   *  all filters". */
  resetActiveSlot(): void {
    this._filterStateByTab[this._activeTab()] = emptyFilterStateSlot();
    this._bump();
  }

  // ── Property getter/setter pairs (Phase 3a.3) ──────────────────────
  // These mirror the public API RatesComponent exposed pre-Phase-3a.
  // The component now has 1-liner proxies that delegate here. Each
  // setter bumps `_version` so net-new consumers (Phase 3b rail,
  // Phase 3d chip stack) re-render on filter changes.
  //
  // 20 pairs total — 19 from the filter slot plus `searchText`. Order
  // matches FilterStateSlot's field declarations.

  get selectedStates(): string[] { return this.activeSlot().selectedStates; }
  set selectedStates(v: string[]) { this.activeSlot().selectedStates = v; this._bump(); }

  get selectedIndexes(): string[] { return this.activeSlot().selectedIndexes; }
  set selectedIndexes(v: string[]) { this.activeSlot().selectedIndexes = v; this._bump(); }

  get selectedSurrYears(): string[] { return this.activeSlot().selectedSurrYears; }
  set selectedSurrYears(v: string[]) { this.activeSlot().selectedSurrYears = v; this._bump(); }

  get selectedCreditingStrategies(): string[] { return this.activeSlot().selectedCreditingStrategies; }
  set selectedCreditingStrategies(v: string[]) { this.activeSlot().selectedCreditingStrategies = v; this._bump(); }

  get selectedStrategyNames(): string[] { return this.activeSlot().selectedStrategyNames; }
  set selectedStrategyNames(v: string[]) { this.activeSlot().selectedStrategyNames = v; this._bump(); }

  get selectedStrategyCombos(): string[] { return this.activeSlot().selectedStrategyCombos; }
  set selectedStrategyCombos(v: string[]) { this.activeSlot().selectedStrategyCombos = v; this._bump(); }

  get contributionAmount(): number | null { return this.activeSlot().contributionAmount; }
  set contributionAmount(v: number | null) { this.activeSlot().contributionAmount = v; this._bump(); }

  get filterRop(): boolean { return this.activeSlot().filterRop; }
  set filterRop(v: boolean) { this.activeSlot().filterRop = v; this._bump(); }

  get filterBailOut(): boolean { return this.activeSlot().filterBailOut; }
  set filterBailOut(v: boolean) { this.activeSlot().filterBailOut = v; this._bump(); }

  get selectedAmBest(): string[] { return this.activeSlot().selectedAmBest; }
  set selectedAmBest(v: string[]) { this.activeSlot().selectedAmBest = v; this._bump(); }

  get filterBonus(): boolean { return this.activeSlot().filterBonus; }
  set filterBonus(v: boolean) { this.activeSlot().filterBonus = v; this._bump(); }

  get filterInverse(): boolean { return this.activeSlot().filterInverse; }
  set filterInverse(v: boolean) { this.activeSlot().filterInverse = v; this._bump(); }

  get selectedRiders(): string[] { return this.activeSlot().selectedRiders; }
  set selectedRiders(v: string[]) { this.activeSlot().selectedRiders = v; this._bump(); }

  get selectedPremiumTypes(): string[] { return this.activeSlot().selectedPremiumTypes; }
  set selectedPremiumTypes(v: string[]) { this.activeSlot().selectedPremiumTypes = v; this._bump(); }

  get filterOwner(): boolean { return this.activeSlot().filterOwner; }
  set filterOwner(v: boolean) { this.activeSlot().filterOwner = v; this._bump(); }

  get filterAnnuitant(): boolean { return this.activeSlot().filterAnnuitant; }
  set filterAnnuitant(v: boolean) { this.activeSlot().filterAnnuitant = v; this._bump(); }

  // ── RILA/VA filter block (2026-06-05) ──────────────────────────
  get selectedBufferLevels(): string[] { return this.activeSlot().selectedBufferLevels; }
  set selectedBufferLevels(v: string[]) { this.activeSlot().selectedBufferLevels = v; this._bump(); }

  get selectedFloorLevels(): string[] { return this.activeSlot().selectedFloorLevels; }
  set selectedFloorLevels(v: string[]) { this.activeSlot().selectedFloorLevels = v; this._bump(); }

  get selectedSegmentDurations(): string[] { return this.activeSlot().selectedSegmentDurations; }
  set selectedSegmentDurations(v: string[]) { this.activeSlot().selectedSegmentDurations = v; this._bump(); }

  get selectedRateTypes(): string[] { return this.activeSlot().selectedRateTypes; }
  set selectedRateTypes(v: string[]) { this.activeSlot().selectedRateTypes = v; this._bump(); }

  get selectedProtectionType(): string | null { return this.activeSlot().selectedProtectionType; }
  set selectedProtectionType(v: string | null) { this.activeSlot().selectedProtectionType = v; this._bump(); }

  get minPremiumMin(): number | null { return this.activeSlot().minPremiumMin; }
  set minPremiumMin(v: number | null) { this.activeSlot().minPremiumMin = v; this._bump(); }

  get minPremiumMax(): number | null { return this.activeSlot().minPremiumMax; }
  set minPremiumMax(v: number | null) { this.activeSlot().minPremiumMax = v; this._bump(); }

  get searchText(): string { return this.activeSlot().searchText; }
  set searchText(v: string) { this.activeSlot().searchText = v; this._bump(); }

  // ── Toggle methods (Phase 3a.4) ──────────────────────────────────
  // Mutate the active tab's slot. Each ends with `_bump()`. Grid
  // re-render is the component's job (calls applyFilters() after the
  // proxy returns) -- the service just owns state.
  //
  // FA- and RILA-specific toggles (onFaRateTermToggle, onFaMvaToggle,
  // onRilaBufferToggle, etc.) STAY on RatesComponent -- they operate
  // on tab-local fields that aren't in FilterStateSlot. Same for
  // onShelfToggle (SessionContextService toggle) and the state-picker
  // helpers (togglePendingState / applyStatePicker — both manage
  // transient UI scratchpads, not FilterStateSlot).

  private _toggleMultiSelect(arr: string[], value: string): string[] {
    const idx = arr.indexOf(value);
    if (idx === -1) return [...arr, value];
    return arr.filter((v) => v !== value);
  }

  onIndexToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedIndexes = this._toggleMultiSelect(slot.selectedIndexes, val);
    this._bump();
  }

  onSurrToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedSurrYears = this._toggleMultiSelect(slot.selectedSurrYears, val);
    this._bump();
  }

  onCreditingToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedCreditingStrategies = this._toggleMultiSelect(slot.selectedCreditingStrategies, val);
    this._bump();
  }

  onStratNameToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedStrategyNames = this._toggleMultiSelect(slot.selectedStrategyNames, val);
    this._bump();
  }

  onStrategyComboToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedStrategyCombos = this._toggleMultiSelect(slot.selectedStrategyCombos, val);
    this._bump();
  }

  onRopToggle(): void {
    const slot = this.activeSlot();
    slot.filterRop = !slot.filterRop;
    this._bump();
  }

  onBailOutToggle(): void {
    const slot = this.activeSlot();
    slot.filterBailOut = !slot.filterBailOut;
    this._bump();
  }

  onAmBestToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedAmBest = this._toggleMultiSelect(slot.selectedAmBest, val);
    this._bump();
  }

  onRidersToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedRiders = this._toggleMultiSelect(slot.selectedRiders, val);
    this._bump();
  }

  onPremiumToggle(val: string): void {
    const slot = this.activeSlot();
    slot.selectedPremiumTypes = this._toggleMultiSelect(slot.selectedPremiumTypes, val);
    this._bump();
  }

  onBonusToggle(): void {
    const slot = this.activeSlot();
    slot.filterBonus = !slot.filterBonus;
    this._bump();
  }

  onInverseToggle(): void {
    const slot = this.activeSlot();
    slot.filterInverse = !slot.filterInverse;
    this._bump();
  }

  onOwnerToggle(): void {
    const slot = this.activeSlot();
    slot.filterOwner = !slot.filterOwner;
    this._bump();
  }

  onAnnuitantToggle(): void {
    const slot = this.activeSlot();
    slot.filterAnnuitant = !slot.filterAnnuitant;
    this._bump();
  }


  // ── Per-tab row data (Phase 3a.5) ────────────────────────────────
  // Mirrors the `_allDataByTab` + `_mainRowDataByTab` Records the
  // component owned pre-3a. Reads/writes to the active tab via the
  // `allData` / `mainRowData` getter/setters; per-tab grid init reads
  // a specific tab via getMainRowDataForTab(t) / setMainRowDataForTab.
  //
  // FA stays separate (`faMainRowData` on the component) -- it never
  // shared with IA/RILA/VA, so it doesn't belong here.
  private _allDataByTab: Record<ProductType, any[]> = {
    FA: [], IA: [], RILA: [], VA: [], ALL: [],
  };
  private _mainRowDataByTab: Record<ProductType, any[]> = {
    FA: [], IA: [], RILA: [], VA: [], ALL: [],
  };

  // Phase 11.4: dataVersion bumps whenever a tab's full dataset is
  // (re)loaded. Data-derived option lists (e.g. the rail carrier picker's
  // company tree) depend on this so they recompute when rows arrive —
  // `_allDataByTab` is a plain object, not a signal, so assigning to it
  // alone would otherwise leave a memoized computed stale.
  private _dataVersion = signal(0);
  readonly dataVersion = this._dataVersion.asReadonly();

  get allData(): any[] { return this._allDataByTab[this._activeTab()] ?? []; }
  set allData(v: any[]) { this._allDataByTab[this._activeTab()] = v; this._dataVersion.update((n) => n + 1); }

  // ── Per-tab catalog products (full profiled-product catalog) ────
  // Distinct from `_allDataByTab`, which only holds rows with live rate-feed
  // data. Populated from the same Benefit API 'both' fetch each enrich*
  // method on RatesComponent already makes for row enrichment — see
  // enrichFaProductIds / enrichIaProductIds / enrichRilaProductIds /
  // enrichVaProductIds. The rail's carrier picker (pickerData) reads this
  // instead of allData so /rates carrier counts match /products' full
  // catalog rather than being limited to carriers with live rate data on
  // file (2026-07-13: was the source of a carrier-count mismatch between
  // the two pages).
  private _catalogProductsByTab: Record<ProductType, CatalogProductPair[]> = {
    FA: [], IA: [], RILA: [], VA: [], ALL: [],
  };

  getCatalogProductsForTab(t: ProductType): CatalogProductPair[] {
    return this._catalogProductsByTab[t] ?? [];
  }
  setCatalogProductsForTab(t: ProductType, v: CatalogProductPair[]): void {
    this._catalogProductsByTab[t] = v;
    this._dataVersion.update((n) => n + 1);
  }

  get mainRowData(): any[] { return this._mainRowDataByTab[this._activeTab()] ?? []; }
  set mainRowData(v: any[]) { this._mainRowDataByTab[this._activeTab()] = v; }

  /** Read a specific tab's row data without changing activeTab. Used
   *  by per-tab onGridReady() wiring (IA/RILA/VA initial setGridOption
   *  call) and any code that needs to enumerate a non-active tab's
   *  rows (e.g. the IA pin builder at the bottom of rates.component). */
  getMainRowDataForTab(t: ProductType): any[] {
    return this._mainRowDataByTab[t] ?? [];
  }
  setMainRowDataForTab(t: ProductType, v: any[]): void {
    this._mainRowDataByTab[t] = v;
  }
  getAllDataForTab(t: ProductType): any[] {
    return this._allDataByTab[t] ?? [];
  }
  setAllDataForTab(t: ProductType, v: any[]): void {
    this._allDataByTab[t] = v;
  }

  // ── Per-tab company filter (Phase 3b.1 — D20 deferred from 3a) ───
  // F16: the Carriers/Products picker stores its applied selection
  // per-tab so that navigating FA→IA→FA preserves the user's filter.
  // The component still owns the ACTIVE-value field (`companyFilter`)
  // because the tab-switch handler needs explicit archive/restore
  // semantics that a proxy-followed-by-active-tab pattern broke once
  // before -- see the comment at rates.component.ts L186. The service
  // just owns the per-tab archive Record.
  private _companyFilterByTab: Record<ProductType, CompanyFilterValue | null> = {
    FA: null, IA: null, RILA: null, VA: null, ALL: null,
  };

  getCompanyFilterForTab(t: ProductType): CompanyFilterValue | null {
    return this._companyFilterByTab[t];
  }

  setCompanyFilterForTab(t: ProductType, v: CompanyFilterValue | null): void {
    this._companyFilterByTab[t] = v;
    // _bump() is needed here so footprints() (which reads version() as its
    // reactive dependency) re-evaluates when carrier state changes. The
    // original "no _bump()" comment referred to the proxy ordering concern
    // (a proxy that auto-routed to the active tab's slot once wrote to the
    // wrong tab); that concern is about companyFilter being a plain field on
    // the component, not about bumping version. Bumping version here is safe:
    // the grid re-apply is driven by applyFilters() (called by the component's
    // onCompanyProductFilter after this), not by version(). P0-1 fix 2026-06-01.
    this._bump();
  }

  // ── FA-specific filter state (Phase 11.5) ───────────────────────
  // FA-only filters that were plain fields on RatesComponent. Lifted to
  // signals here so the rail can read them reactively + write them; the
  // component proxies its `faSelectedRateTerms` / `faSelectedSurrYears` /
  // `faFilterMva` / `faFilterQualified` fields to these (single source of
  // truth, no drift). Not per-tab — FA is the only tab that uses them.
  // These are the AUTHORITATIVE fields read by the FA grid's
  // doesExternalFilterPass; the legacy faFilterRateTerm / faFilterSurrYears
  // (single-value) are vestigial and stay on the component untouched.
  private _faRateTerms = signal<number[]>([]);
  private _faSurrYears = signal<number[]>([]);
  private _faBonusYears = signal<number[]>([]);
  private _faMva = signal(false);
  private _faQualified = signal(false);
  readonly faRateTerms = this._faRateTerms.asReadonly();
  readonly faSurrYears = this._faSurrYears.asReadonly();
  readonly faBonusYears = this._faBonusYears.asReadonly();
  readonly faMva = this._faMva.asReadonly();
  readonly faQualified = this._faQualified.asReadonly();
  // These deliberately do NOT _bump() (version drives slot-derived computeds;
  // FA state has its own signals), so each schedules its own persist.
  setFaRateTerms(v: number[]): void { this._faRateTerms.set(v); this._schedulePersist(); }
  setFaSurrYears(v: number[]): void { this._faSurrYears.set(v); this._schedulePersist(); }
  setFaBonusYears(v: number[]): void { this._faBonusYears.set(v); this._schedulePersist(); }
  setFaMva(v: boolean): void { this._faMva.set(v); this._schedulePersist(); }
  setFaQualified(v: boolean): void { this._faQualified.set(v); this._schedulePersist(); }

  // ── Tab-switch request (Phase 3b.4) ──────────────────────────────
  // A one-shot side-effect trigger. RatesRailComponent's pill click
  // calls requestTabSwitch(t); RatesComponent's constructor effect
  // observes the request and invokes its existing setProductType()
  // (which runs the full archive→switch→restore chain), then calls
  // ackTabSwitchRequest() to clear the signal so the same tab can be
  // requested again later.
  //
  // No _bump() -- this isn't FilterStateSlot state, it's a coordination
  // primitive. _activeTab gets _bump()'d separately by setActiveTab.
  private _tabSwitchRequest = signal<ProductType | null>(null);
  readonly tabSwitchRequest = this._tabSwitchRequest.asReadonly();

  requestTabSwitch(t: ProductType): void {
    this._tabSwitchRequest.set(t);
  }

  ackTabSwitchRequest(): void {
    this._tabSwitchRequest.set(null);
  }

  // ── Search apply request (Phase 3c.1) ────────────────────────────
  // A one-shot side-effect trigger from either chrome (legacy
  // .s2-filter-bar input OR the new rail input). Both call
  // requestSearchApply(v) which writes filterState.searchText (proxy
  // through to the active slot's FilterStateSlot.searchText) AND sets
  // the _searchApplyRequest signal. RatesComponent's constructor effect
  // observes the signal, debounces 150ms (clearTimeout pattern), calls
  // the grid-API chain, then ack()s.
  //
  // The signal is just the coordination primitive -- searchText itself
  // remains the single source of truth for the filter value.
  private _searchApplyRequest = signal<string | null>(null);
  readonly searchApplyRequest = this._searchApplyRequest.asReadonly();

  requestSearchApply(v: string): void {
    this.searchText = v;
    this._searchApplyRequest.set(v);
  }

  ackSearchApplyRequest(): void {
    this._searchApplyRequest.set(null);
  }

  // ── Public mutation helpers (Phase 3e.1) ─────────────────────────
  // Pickers in rail/pickers/ need to write a FilterStateSlot field
  // by name (e.g. selectedIndexes) without coupling each picker to a
  // specific setter. `setSlotField` writes the active tab's slot and
  // bumps version. `bumpVersion` is exposed for cases where the
  // mutation is unavoidable from outside (e.g. an array mutated in
  // place) -- avoid when possible; prefer setSlotField.
  setSlotField<K extends keyof FilterStateSlot>(field: K, value: FilterStateSlot[K]): void {
    const slot = this.activeSlot();
    (slot as Record<K, FilterStateSlot[K]>)[field] = value;
    this._bump();
  }

  bumpVersion(): void {
    this._bump();
  }

  // ── Apply-filters request (Phase 3d.1) ───────────────────────────
  // A counter signal that any rail-driven mutation can bump to ask
  // RatesComponent to re-fire applyFilters() on the active grid. Same
  // coordination shape as D22 / D27 but uses a number bump instead of
  // a nullable string -- subsequent calls re-fire the effect because
  // the value changes. No ack needed.
  private _applyFiltersRequest = signal<number>(0);
  readonly applyFiltersRequest = this._applyFiltersRequest.asReadonly();

  requestApplyFilters(): void {
    this._applyFiltersRequest.update((n) => n + 1);
  }

  /** Clear ONE dimension on the active tab's slot. Used by the rail's
   *  chip × button. Caller should follow up with requestApplyFilters()
   *  to re-run the grid filter. Unknown keys are no-ops (defensive
   *  against future chips that ship before their clear is wired). */
  clearAppliedDimension(key: string): void {
    const slot = this.activeSlot();
    switch (key) {
      case 'state':         slot.selectedStates = [];               break;
      case 'index':         slot.selectedIndexes = [];              break;
      case 'combo':         slot.selectedStrategyCombos = [];       break;
      case 'surr':          slot.selectedSurrYears = [];            break;
      case 'mktg':          slot.selectedCreditingStrategies = [];  break;
      case 'strat':         slot.selectedStrategyNames = [];        break;
      case 'contribution':  slot.contributionAmount = null;         break;
      case 'rop':           slot.filterRop = false;                 break;
      case 'bail':          slot.filterBailOut = false;             break;
      case 'bonus':         slot.filterBonus = false;               break;
      case 'inverse':       slot.filterInverse = false;             break;
      case 'am-best':       slot.selectedAmBest = [];               break;
      case 'riders':        slot.selectedRiders = [];               break;
      case 'prem-type':     slot.selectedPremiumTypes = [];         break;
      // RILA/VA filter block dimensions (2026-06-05)
      case 'buffer-level':     slot.selectedBufferLevels = [];                                 break;
      case 'floor-level':      slot.selectedFloorLevels = [];                                  break;
      case 'segment-duration': slot.selectedSegmentDurations = [];                             break;
      case 'rate-type':        slot.selectedRateTypes = [];                                    break;
      case 'protection-type':  slot.selectedProtectionType = null;                             break;
      case 'min-premium':      slot.minPremiumMin = null; slot.minPremiumMax = null;           break;
      // 'company' chip is component-owned (companyFilter is not in
      // FilterStateSlot) -- the rail never produces it via buildAppliedChips.
      default:              return;
    }
    this._bump();
  }

  /**
   * Clear one dimension keyed by the panel-side `DimKey` (rates-filter-panel.service).
   *
   * This is the canonical click-to-clear used by the page-header "footprint"
   * chips (Item 1 of the 2026-06-01 batch). It handles every DimKey the rail's
   * `activeAppliedFilters` computed can produce, including:
   *
   *   - Slot-resident dims (selectedXxx / filterXxx fields)
   *   - Cross-tab special cases:
   *       · `carrier`     -- routes through `requestCompanyFilter(null)` so the
   *         component's existing ordering-safe `onCompanyProductFilter()` runs
   *         (it sets `companyFilter`, archives via setCompanyFilterForTab, and
   *         calls applyFilters). Clearing via slot alone would leave a stale
   *         draft in `_companyFilterByTab`.
   *       · `faRateTerm` / `faSurrYears` / `faBonusYears` -- clears `_faRateTerms` /
   *         `_faSurrYears` / `_faBonusYears` signals directly.
   *       · `faMva` / `faQualified`     -- clears the FA-specific signals directly.
   *       · `mva`                       -- no slot field on IA/RILA/VA today;
   *         only FA uses it as `faMva`. The legacy `_faMva` clear runs as a
   *         conservative fallback.
   *
   * Unknown keys are no-ops (defensive). Callers do NOT need to follow up with
   * `requestApplyFilters()` -- this method bumps version + invokes the company-
   * filter request, both of which the rates component already wires to apply.
   * For non-company dims the caller should still call requestApplyFilters() to
   * re-fire the grid filter (matches the existing `clearAppliedDimension`
   * contract).
   */
  /** Bumps whenever clearDim() runs (i.e. an Active-Filters footprint chip is
   *  cleared). The rail listens so it can collapse that dimension's expanded
   *  section/picker. Carries the cleared dim + a monotonic tick. */
  private _dimClearedRequest = signal<{ dim: string; tick: number }>({ dim: '', tick: 0 });
  readonly dimClearedRequest = this._dimClearedRequest.asReadonly();

  clearDim(dim: string): void {
    // Notify collapse listeners first (fires for every dim, incl. the carrier
    // early-return path below).
    this._dimClearedRequest.update((r) => ({ dim, tick: r.tick + 1 }));
    const slot = this.activeSlot();
    switch (dim) {
      case 'carrier':
        // Route through the request signal so the component's ordering-safe
        // onCompanyProductFilter() runs. It clears the active field, archives
        // null into the per-tab Record, and calls applyFilters().
        this.requestCompanyFilter(null);
        return; // No _bump(); the request handler will trigger re-render.
      case 'states':        slot.selectedStates = [];                            break;
      case 'index':         slot.selectedIndexes = [];                           break;
      case 'crediting':     slot.selectedStrategyNames = [];                     break;
      case 'combo':         slot.selectedStrategyCombos = [];                    break;
      case 'mktg':          slot.selectedCreditingStrategies = [];               break;
      case 'premium':       slot.contributionAmount = null;                      break;
      case 'surrYears':     slot.selectedSurrYears = [];                         break;
      case 'amBest':        slot.selectedAmBest = [];                            break;
      case 'premiumType':   slot.selectedPremiumTypes = [];                      break;
      case 'riders':        slot.selectedRiders = [];                            break;
      case 'rop':           slot.filterRop = false;                              break;
      // Without this, clearing the Bail Out chip fell through to `default`:
      // the component cleared its own mirror (so the grid re-filtered) but the
      // slot kept filterBailOut = true and never bumped, so the rail's Bail Out
      // toggle still read as selected.
      case 'bail':          slot.filterBailOut = false;                          break;
      case 'bonus':         slot.filterBonus = false;                            break;
      case 'inverse':       slot.filterInverse = false;                          break;
      case 'mva':           this._faMva.set(false);                              break;
      case 'owner':         slot.filterOwner = false;                            break;
      case 'annuitant':     slot.filterAnnuitant = false;                        break;
      case 'faRateTerm':    this._faRateTerms.set([]);                           break;
      case 'faSurrYears':   this._faSurrYears.set([]);                           break;
      case 'faBonusYears':  this._faBonusYears.set([]);                          break;
      case 'faMva':         this._faMva.set(false);                              break;
      case 'faQualified':   this._faQualified.set(false);                        break;
      // RILA/VA filter block dimensions (2026-06-05)
      case 'bufferLevel':      slot.selectedBufferLevels = [];                                 break;
      case 'floorLevel':       slot.selectedFloorLevels = [];                                  break;
      case 'segmentDuration':  slot.selectedSegmentDurations = [];                             break;
      case 'rateType':         slot.selectedRateTypes = [];                                    break;
      case 'protectionType':   slot.selectedProtectionType = null;                             break;
      case 'minPremium':       slot.minPremiumMin = null; slot.minPremiumMax = null;           break;
      default:              return;
    }
    this._bump();
  }

  // ── Reset-all-filters request (Phase 10.8.1) ────────────────────
  // Counter signal the rail bumps when the user clicks "Clear all" at
  // the rail top. RatesComponent observes the bump and runs the
  // existing resetAllFilters() body, which clears (a) the active slot,
  // (b) tab-local FA-specific fields, (c) the per-tab companyFilter,
  // (d) sort + view state. Only the parent can do (b)/(c)/(d); the
  // service slot is a subset of "everything applied". Same coordination
  // shape as shelfToggleRequest below.
  private _resetAllRequest = signal<number>(0);
  readonly resetAllRequest = this._resetAllRequest.asReadonly();

  requestResetAll(): void {
    this._resetAllRequest.update((n) => n + 1);
  }

  // ── Seed-from-case request (case-as-saved-search) ───────────────
  // The rail's Criteria "Reset" bumps this; RatesComponent observes it
  // and re-runs seedFiltersFromCase() (which writes the active tab's
  // slot from the loaded case + applies). Counter signal, no ack.
  private _seedFromCaseRequest = signal<number>(0);
  readonly seedFromCaseRequest = this._seedFromCaseRequest.asReadonly();

  requestSeedFromCase(): void {
    this._seedFromCaseRequest.update((n) => n + 1);
  }

  // Count of products on the loaded case's favorites shortlist. Published by
  // RatesComponent (syncCaseCandidates / addRowToCase) so the rail can show
  // "★ N/MAX on case".
  private _caseFavoriteCount = signal<number>(0);
  readonly caseFavoriteCount = this._caseFavoriteCount.asReadonly();
  setCaseFavoriteCount(n: number): void {
    this._caseFavoriteCount.set(n);
  }

  // ── Displayed (post-filter) row count ───────────────────────────
  // Published by RatesComponent.updateDisplayedCount() after each grid
  // filter pass so the rail's Criteria "X of Y fit" count can read it
  // without grid-api access. Plain signal; rates owns the write.
  private _displayedCount = signal<number>(0);
  readonly displayedCount = this._displayedCount.asReadonly();
  setDisplayedCount(n: number): void {
    this._displayedCount.set(n);
  }

  // Count of rows matching the loaded case (the "fit" number) — equals
  // displayedCount in Fit mode, but in Show-all mode it's the matching subset
  // among all shown rows. Published by updateDisplayedCount().
  private _caseFitCount = signal<number>(0);
  readonly caseFitCount = this._caseFitCount.asReadonly();
  setCaseFitCount(n: number): void {
    this._caseFitCount.set(n);
  }

  // ── Company-filter request (Phase 11.4) ─────────────────────────
  // The rail's carrier picker can't write companyFilter directly: it's
  // a PLAIN field on RatesComponent with documented ordering-bug history
  // (a proxy that auto-routed to the active tab's slot once wrote to the
  // wrong tab). So the rail bumps this channel with the requested value
  // and the component constructor effect runs the EXISTING ordering-safe
  // onCompanyProductFilter() (sets the field + archives + applyFilters).
  // The `tick` forces the effect even when value is structurally equal
  // (e.g. re-selecting after a clear).
  private _companyFilterRequest = signal<{ value: CompanyFilterValue | null; tick: number }>({ value: null, tick: 0 });
  readonly companyFilterRequest = this._companyFilterRequest.asReadonly();

  requestCompanyFilter(value: CompanyFilterValue | null): void {
    // Write to the per-tab archive immediately so reactive consumers
    // (e.g. RailCarrierPickerComponent.selectedCompanies) read the correct
    // value when their computed recomputes from the tick bump below —
    // without waiting for the RatesComponent effect to call setCompanyFilterForTab.
    this._companyFilterByTab[this._activeTab()] = value;
    this._companyFilterRequest.update((r) => ({ value, tick: r.tick + 1 }));
    // No _bump() here (see above), so persistence needs its own hook — without
    // it a carrier pick, or the `carrier` branch of clearDim(), was the one
    // filter that did not survive a refresh.
    this._schedulePersist();
  }

  // ── Shelf-toggle request (Phase 3f.1) ───────────────────────────
  // A counter signal the rail bumps when the user clicks the My Shelf
  // pill. The component constructor effect observes the bump and runs
  // the EXISTING onShelfToggle() body (sessionCtx.toggleShelfMode +
  // applyFilters + refreshCells on both grids). Same coordination
  // shape as D33 applyFiltersRequest, just a separate channel since
  // shelf needs cell refresh which the apply-only request skips.
  private _shelfToggleRequest = signal<number>(0);
  readonly shelfToggleRequest = this._shelfToggleRequest.asReadonly();

  requestShelfToggle(): void {
    this._shelfToggleRequest.update((n) => n + 1);
  }

  // ── Per-tab applied-filter count (Phase 3a.7) ────────────────────
  // Counts active dimensions, not total selected values within a
  // dimension -- so a single-state pick + an index pick + a single
  // rider pick = 3.
  countForTab(t: ProductType): number {
    return countActiveFilters(this._filterStateByTab[t]);
  }

  // ── Per-tab row count (Phase 3a.8) ───────────────────────────────
  // Returns the total number of loaded rows for a given tab.
  // Uses `_allDataByTab` (unfiltered full dataset per tab) so counts
  // are available for non-active tabs without switching tabs. Reactive
  // via `dataVersion` signal — callers should read that signal first in
  // their computed so Angular tracks the dependency.
  rowCountForTab(t: ProductType): number {
    return this._allDataByTab[t]?.length ?? 0;
  }
}

// ── Applied chip shape (Phase 3d) ──────────────────────────────────
// One entry per active filter dimension. Consumed by both the legacy
// `activeFilters` getter on RatesComponent (which appends carriers +
// FA-specific chips) and the new rail's chip stack. The shape stays
// data-only -- no closures -- so the helper is unit-testable.
export interface AppliedChip {
  key: string;        // 'state' | 'index' | 'surr' | etc. -- stable id for clear
  label: string;      // 'States' | 'Index' | 'Surrender Years' | 'Carriers' | ...
  value: string;      // 'NY · NJ · CT' | 'S&P 500' | '7' | '8 carriers' | ...
  isMono?: boolean;   // render value in Roboto Mono (numeric / id-like)
}

/** Pure helper -- given a FilterStateSlot, returns the list of active
 *  chips for the 13 tab-agnostic dimensions. Order matches the legacy
 *  `activeFilters` getter on RatesComponent so both consumers display
 *  the same sequence. NO closures, NO Angular DI -- testable in
 *  isolation. Consumers supply their own clear handlers via the
 *  chip's `key` (calling service.clearAppliedDimension(key)). */
export function buildAppliedChips(slot: FilterStateSlot): AppliedChip[] {
  const out: AppliedChip[] = [];
  const summarize = (arr: string[], plural: string): string => {
    if (arr.length === 1) return arr[0];
    if (arr.length <= 3) return arr.join(', ');
    return `${arr.length} ${plural}`;
  };
  if (slot.selectedStates.length) {
    const v = slot.selectedStates.length <= 3
      ? slot.selectedStates.join(' · ')
      : `${slot.selectedStates.length} states`;
    out.push({ key: 'state', label: slot.selectedStates.length === 1 ? 'State' : 'States', value: v });
  }
  if (slot.selectedIndexes.length)              out.push({ key: 'index',  label: 'Index',          value: summarize(slot.selectedIndexes, 'indexes') });
  if (slot.selectedStrategyCombos.length)       out.push({ key: 'combo',  label: 'Strategy',       value: summarize(slot.selectedStrategyCombos, 'combos') });
  if (slot.selectedSurrYears.length)            out.push({ key: 'surr',   label: 'Surrender Years', value: summarize(slot.selectedSurrYears, 'yrs'), isMono: true });
  if (slot.selectedCreditingStrategies.length)  out.push({ key: 'mktg',   label: 'Mktg Name',      value: summarize(slot.selectedCreditingStrategies, 'names') });
  if (slot.selectedStrategyNames.length)        out.push({ key: 'strat',  label: 'Strategy Name',  value: summarize(slot.selectedStrategyNames, 'strategies') });
  if (slot.contributionAmount != null)          out.push({ key: 'contribution', label: 'Premium', value: '$' + slot.contributionAmount.toLocaleString(), isMono: true });
  if (slot.filterRop)     out.push({ key: 'rop',     label: 'ROP',      value: 'on' });
  if (slot.filterBailOut) out.push({ key: 'bail',    label: 'Bail Out', value: 'on' });
  if (slot.filterBonus)   out.push({ key: 'bonus',   label: 'Bonus',    value: 'on' });
  if (slot.filterInverse) out.push({ key: 'inverse', label: 'Inverse',  value: 'on' });
  if (slot.selectedAmBest.length)               out.push({ key: 'am-best',   label: 'AM Best',       value: summarize(slot.selectedAmBest, 'ratings') });
  if (slot.selectedRiders.length)               out.push({ key: 'riders',    label: 'Riders',        value: summarize(slot.selectedRiders, 'riders') });
  if (slot.selectedPremiumTypes.length)         out.push({ key: 'prem-type', label: 'Premium Type',  value: summarize(slot.selectedPremiumTypes, 'types') });
  if (slot.selectedBufferLevels.length)     out.push({ key: 'buffer-level',     label: 'Buffer Level',     value: summarize(slot.selectedBufferLevels, 'levels') });
  if (slot.selectedFloorLevels.length)      out.push({ key: 'floor-level',      label: 'Floor Level',      value: summarize(slot.selectedFloorLevels, 'levels') });
  if (slot.selectedSegmentDurations.length) out.push({ key: 'segment-duration', label: 'Segment Duration', value: summarize(slot.selectedSegmentDurations, 'durations') });
  if (slot.selectedRateTypes.length)        out.push({ key: 'rate-type',        label: 'Rate Type',        value: summarize(slot.selectedRateTypes, 'types') });
  if (slot.selectedProtectionType)          out.push({ key: 'protection-type',  label: 'Protection',       value: slot.selectedProtectionType });
  if (slot.minPremiumMin != null || slot.minPremiumMax != null) {
    const lo = slot.minPremiumMin != null ? '$' + slot.minPremiumMin.toLocaleString() : '–';
    const hi = slot.minPremiumMax != null ? '$' + slot.minPremiumMax.toLocaleString() : '–';
    out.push({ key: 'min-premium', label: 'Min Premium', value: `${lo}–${hi}`, isMono: true });
  }
  return out;
}

/** Counts the number of active filter dimensions in a slot. Exported
 *  for testability; the only runtime caller is service.countForTab. */
export function countActiveFilters(s: FilterStateSlot): number {
  let n = 0;
  if (s.selectedStates.length > 0)                       n++;
  if (s.selectedIndexes.length > 0)                      n++;
  if (s.selectedSurrYears.length > 0)                    n++;
  if (s.selectedCreditingStrategies.length > 0)          n++;
  if (s.selectedStrategyNames.length > 0)                n++;
  if (s.selectedStrategyCombos.length > 0)               n++;
  if (s.contributionAmount != null)                      n++;
  if (s.filterRop)                                       n++;
  if (s.filterBailOut)                                   n++;
  if (s.selectedAmBest.length > 0)                       n++;
  if (s.filterBonus)                                     n++;
  if (s.filterInverse)                                   n++;
  if (s.selectedRiders.length > 0)                       n++;
  if (s.selectedPremiumTypes.length > 0)                 n++;
  if (s.filterOwner)                                     n++;
  if (s.filterAnnuitant)                                 n++;
  if (s.selectedBufferLevels.length > 0)                 n++;
  if (s.selectedFloorLevels.length > 0)                  n++;
  if (s.selectedSegmentDurations.length > 0)             n++;
  if (s.selectedRateTypes.length > 0)                    n++;
  if (s.selectedProtectionType)                          n++;
  if (s.minPremiumMin != null || s.minPremiumMax != null) n++;
  if (s.searchText)                                      n++;
  return n;
}
